import * as React from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button
} from '@mui/material';
import {
  Add as AddIcon
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { SummarizationCardProps, ContentNode } from '@/types';
import ContentNodeItem from '../nodes/ContentNodeItem';

interface EditState {
  node: ContentNode | null;
  prompt: string;
}

export default function SummarizationCard({ 
  contentNodes,
  setContentNodes,
  validationError
}: SummarizationCardProps) {
  const [editState, setEditState] = React.useState<EditState>({
    node: null,
    prompt: ''
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = contentNodes.findIndex((item: ContentNode) => item.id === active.id);
      const newIndex = contentNodes.findIndex((item: ContentNode) => item.id === over?.id);
      setContentNodes(arrayMove(contentNodes, oldIndex, newIndex));
    }
  };

  const handleAddNode = () => {
    // Don't add new node if any item is being edited
    if (editState.node) {
      return;
    }
    
    const newNode: ContentNode = {
      id: `node-${crypto.randomUUID()}`,
      prompt: ''
    };
    setContentNodes([...contentNodes, newNode]);
    // Automatically start editing the new node
    setEditState({
      node: newNode,
      prompt: ''
    });
  };

  const handleEditNode = (node: ContentNode) => {
    // If there's already an editing node, cancel it first
    if (editState.node && editState.node.id !== node.id) {
      setEditState({
        node: null,
        prompt: ''
      });
    }
    setEditState({
      node: node,
      prompt: node.prompt
    });
  };

  const handleSaveNode = (nodeId: string, newPrompt: string) => {
    // Don't save if prompt is empty or only whitespace
    if (!newPrompt.trim()) {
      return;
    }
    
    setContentNodes(contentNodes.map(node => 
      node.id === nodeId ? { ...node, prompt: newPrompt.trim() } : node
    ));
    setEditState({
      node: null,
      prompt: ''
    });
  };

  const handleCancelEdit = () => {
    // If the current editing node has an empty prompt, remove it from the list
    if (editState.node && !editState.prompt.trim()) {
      setContentNodes(contentNodes.filter(node => node.id !== editState.node!.id));
    }
    setEditState({
      node: null,
      prompt: ''
    });
  };

  const handleDeleteNode = (nodeId: string) => {
    setContentNodes(contentNodes.filter(node => node.id !== nodeId));
  };

  // Check for validation errors
  const hasNoValidNodes = contentNodes.length === 0 || !contentNodes.some(node => node.prompt && node.prompt.trim());
  
  // Determine the validation error to show
  const getValidationError = () => {
    if (validationError) return validationError;
    if (hasNoValidNodes) return 'Please add at least one content node with a prompt.';
    return null;
  };

  const currentValidationError = getValidationError();

  return (
    <>
      <Paper sx={{ 
        padding: 4,
        backgroundColor: 'background.paper',
        borderRadius: 2,
        boxShadow: 1,
        maxWidth: 800
      }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            color: 'text.primary', 
            fontWeight: 'bold',
            marginBottom: 2,
            fontSize: '1.5rem'
          }}
        >
          Summarization Steps
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            color: 'text.secondary',
            marginBottom: 4,
            fontSize: '0.875rem',
            lineHeight: 1.6
          }}
        >
          <strong>Content Nodes</strong> are run in sequence. The input to the first node will be the document to be summarized. 
          Input for the following nodes will be the output of the previous node. The output of the last node 
          will be used as the summary.
        </Typography>
        
        {/* Validation Error Display */}
        {currentValidationError && (
          <Box sx={{ 
            marginBottom: 2,
            padding: 2,
            backgroundColor: 'error.lighter',
            border: '1px solid',
            borderColor: 'error.main',
            borderRadius: 1
          }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'error.dark',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              {currentValidationError}
            </Typography>
          </Box>
        )}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext 
            items={contentNodes.map(node => node.id)}
            strategy={verticalListSortingStrategy}
          >
            <Stack>
              {contentNodes.map((node, index) => (
                <Box key={node.id} sx={{ position: 'relative' }}>
                  {/* Arrow between nodes */}
                  {index > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginBottom: 1,
                        '&::before': {
                          content: '""',
                          width: 0,
                          height: 0,
                          borderLeft: '8px solid transparent',
                          borderRight: '8px solid transparent',
                          borderTop: '12px solid',
                          borderTopColor: 'divider',
                        }
                      }}
                    />
                  )}
                  <ContentNodeItem
                    node={node}
                    onEdit={handleEditNode}
                    onDelete={handleDeleteNode}
                    onSave={handleSaveNode}
                    onCancel={handleCancelEdit}
                    isEditing={editState.node?.id === node.id}
                    editPrompt={editState.prompt}
                    setEditPrompt={(prompt) => setEditState(prev => ({ ...prev, prompt }))}
                    validationError={(!node.prompt || !node.prompt.trim()) ? 'Prompt cannot be empty' : undefined}
                  />
                </Box>
              ))}
            </Stack>
          </SortableContext>
        </DndContext>

        <Box sx={{ marginTop: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddNode}
            disabled={Boolean(editState.node)}
            sx={{
              minWidth: 120
            }}
          >
            Add Content Node
          </Button>
        </Box>
      </Paper>
    </>
  )
}